import { CompanyRaw } from 'state/companies';
import { GROUP_TYPE } from 'utils/constants';
import { Region, State } from 'utils/constants/location';

// export type EmailFrequency = 'daily' | 'weekly' | 'monthly';

export enum EmailFrequency {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
}

export type GroupConfigSource = 'group_config' | 'scoutbot';

export type Item = {
  id: number;
  name: string;
  label?: string;
  type?: number;
  lead_count?: number;
  categories?: number[];
  statuses?: number[];
  description?: string;
  city?: number;
};

export type VerticalRaw = {
  id: number;
  name: string;
  sector: Item;
};

export type District = {
  id: number;
  lud_code: string;
};

export type ParentItem = Item & { children?: Item[] };

export type TStartDate =
  | '7_days_ago'
  | '30_days_ago'
  | '3_months_ago'
  | '6_months_ago'
  | '1_years_ago'
  | '3_years_ago'
  | 'anytime'
  | '';

export type Group = {
  id: number | string;
  type?: GROUP_TYPE;
  name?: string;
  notifications: {
    enabled: boolean;
    timezone?: string;
  };
  email_frequency: EmailFrequency;
  districts: number[];
  zoneExclusions: number[];
  verticals: number[];
  verticalsExclusions: number[];
  companies: number[];
  keywords: string;
  exclusions?: string;
  mercatorMetropolitanId: Region | null;
  mercator_metropolitan_ids: Region[];
  state_code: State | null;
  cityIds: number[];
  county_codes: string[];
  triggers: {
    cityId: number;
    documentTypes: {
      id: number;
      categories: number[];
      statuses: number[];
    }[];
  }[];
  past_project_stage: {
    cityId: number;
    documentTypes: {
      id: number;
      categories: number[];
      statuses: number[];
    }[];
  }[];
  exclude_past_project_stage: {
    cityId: number;
    documentTypes: {
      id: number;
      categories: number[];
      statuses: number[];
    }[];
  }[];
  cost: {
    min: number;
    max: number;
  };
  project_cost_include_unknown: boolean;
  status: string;
  startDate: TStartDate;
  endDate: string;
  realEstate: boolean;
  brokerData: boolean;
  documentUrl: boolean;
  date_created_start?: TStartDate;
  date_created_end?: string;
  workTypes: string[];
  workTypesExclusions: string[];
  project_size_min: number | null;
  project_size_max: number | null;
  project_size_include_unknown: boolean;
  parcel_size_min: number | null;
  parcel_size_max: number | null;
  parcel_size_include_unknown: boolean;
  companyKeywords: string;
  companyKeywordsExclusions: string;
  company_stakeholder_types: string;
  company_stakeholder_types_exclusions: string;
  polygon_bounds?: string | null;
};

// API format
export type GroupRaw = {
  id?: number;
  name: string;
  type: GROUP_TYPE;
  districts: number[];
  zone_exclusions: number[];
  verticals: number[];
  verticals_exclusions: number[];
  // temporary `any` until API has consistent format
  companies: any;
  min_project_cost: number;
  max_project_cost: number;
  project_cost_include_unknown: boolean;
  keywords: string;
  email_frequency: EmailFrequency;
  emails: string[];
  mercator_metropolitan_id: Region;
  mercator_metropolitan_ids: Region[];
  state_code: State;
  city_ids: number[];
  county_codes?: string[];
  triggers: {
    city_id: number;
    document_types: {
      document_type_id: number;
      categories: number[];
      document_statuses: number[];
    }[];
  }[];
  past_project_stages: {
    city_id: number;
    document_types: {
      document_type_id: number;
      categories: number[];
      document_statuses: number[];
    }[];
  }[];
  exclude_past_project_stages: {
    city_id: number;
    document_types: {
      document_type_id: number;
      categories: number[];
      document_statuses: number[];
    }[];
  }[];
  status: string;
  startDate: string;
  endDate: string;
  real_estate_data_available: boolean;
  broker_data_available: boolean;
  document_url_available: boolean;
  exclusions?: string;
  work_types: string[];
  work_types_exclusions: string[];
  project_size_min: number | null;
  project_size_max: number | null;
  project_size_include_unknown: boolean;
  parcel_size_min: number | null;
  parcel_size_max: number | null;
  parcel_size_include_unknown: boolean;
  company_keywords: string;
  company_keywords_exclusions: string;
  company_stakeholder_types: string;
  company_stakeholder_types_exclusions: string;
  polygon_bounds?: string | null;
};

export type GroupRawResponse = {
  id: number;
  name: string;
  city: number;
  type: GROUP_TYPE;
  districts: District[];
  zone_exclusions: District[];
  verticals: VerticalRaw[];
  verticals_exclusions: VerticalRaw[];
  companies: CompanyRaw[];
  min_project_cost: number;
  max_project_cost: number;
  project_cost_include_unknown: boolean;
  keywords: string;
  email_frequency: EmailFrequency;
  emails: string[];
  mercator_metropolitan_id: Region;
  mercator_metropolitan_ids: Region[];
  state_code: State;
  city_ids: number[];
  county_codes?: string[];
  triggers: {
    city_id: number;
    document_types: {
      document_type_id: number;
      categories: number[];
      document_statuses: number[];
    }[];
  }[];
  past_project_stages: {
    city_id: number;
    document_types: {
      document_type_id: number;
      categories: number[];
      document_statuses: number[];
    }[];
  }[];
  exclude_past_project_stages: {
    city_id: number;
    document_types: {
      document_type_id: number;
      categories: number[];
      document_statuses: number[];
    }[];
  }[];
  status: string;
  startDate: '';
  endDate: string;
  real_estate_data_available: boolean;
  broker_data_available: boolean;
  document_url_available: boolean;
  is_notification_enabled: boolean;
  exclusions?: string;
  work_types: string[];
  work_types_exclusions: string[];
  project_size_min: number | null;
  project_size_max: number | null;
  project_size_include_unknown: boolean;
  parcel_size_min: number | null;
  parcel_size_max: number | null;
  parcel_size_include_unknown: boolean;
  company_keywords: string;
  company_keywords_exclusions: string;
  company_stakeholder_types: string;
  company_stakeholder_types_exclusions: string;
  polygon_bounds?: string | null;
  source?: GroupConfigSource;
  scoutbot?: {
    adk_query_id: string;
    version: string | number | null;
    // Raw ES query bodies, keyed by index alias (e.g. "project_documents_*_alias").
    queries: Record<string, { query: Record<string, unknown> }>;
    query_md5: string;
  } | null;
};

export type TriggerType = 'triggers' | 'pastStage' | 'excludePastStage';

export type EventTriggersProps = {
  city: Item;
  doc: Item;
  type: TriggerType;
};

export type CityUpdateKeys = {
  triggers?: boolean;
  exclude_past_project_stage?: boolean;
  past_project_stage?: boolean;
};

declare global {
  interface Window {
    heap: any;
    hj: any;
    dataLayer: Array<Record<string, unknown>>;
  }
}
